import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Divider,
  useColorModeValue,
  Link,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";
import LegalHeader from "./LegalHeader";
import LandingFooter from "./LandingFooter";

const PrivacyPolicy = () => {
  const bgColor = useColorModeValue("white", "gray.900");
  const textColor = useColorModeValue("gray.700", "gray.300");
  const headingColor = useColorModeValue("gray.800", "white");
  const dividerColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box bg={bgColor} minH="100vh">
      <LegalHeader />
      <Box pt="90px" pb="60px">
        <Container maxW="1000px">
          <VStack spacing={8} align="flex-start">
            <Heading as="h1" size="2xl" color={headingColor} fontWeight="bold">
              Privacy Policy
            </Heading>
            <Text fontSize="md" color={textColor}>
              Updated 01/26/2025
            </Text>

            <Text color={textColor}>
              This Privacy Policy describes how Awning Technologies, Inc. (&quot;Awning,&quot; &quot;we&quot;, &quot;us&quot; or &quot;our&quot;) handles personal information that we collect through our digital properties that link to this Privacy Policy (collectively, the &quot;Service&quot;).
            </Text>

            <Text color={textColor}>
              Awning provides a go to market platform that uses automation and Artificial Intelligence to help businesses optimize their customer pipeline using customer information such as messages, emails, calls, etc. This Privacy Policy does not apply to information that we process on behalf of our enterprise customers (such as businesses and other organizations) while providing our services to them. Our use of information that we process on behalf of our enterprise customers may be governed by our agreements with such customers. If you have concerns regarding your personal information that we process on behalf of an enterprise customer, please direct your concerns to that enterprise customer.
            </Text>

            <Divider borderColor={dividerColor} />

            <VStack spacing={6} align="flex-start" width="full">
              <Box width="full">
                <Heading as="h2" size="lg" mb={4} color={headingColor}>
                  1. Personal Information We Collect
                </Heading>
                
                <VStack spacing={4} align="flex-start">
                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      1.1. Information You Provide to Us
                    </Heading>
                    <Text color={textColor}>
                      Personal information you may provide to us through the Service or otherwise includes:
                    </Text>
                    <UnorderedList mt={2} spacing={1} pl={5}>
                      <ListItem>
                        <Text color={textColor}>
                          Contact data: First and last name, email address, billing and mailing addresses, professional title and company name, and phone number.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Profile data: Username and password to establish an online account on the Service, biographical details, profile picture, preferences, information about your participation in our surveys, and any other information added to your account profile.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Communications data: Information based on our exchanges with you, including when you contact us through the Service or otherwise.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Marketing data: Preferences for receiving our marketing communications and details about your engagement with them.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          User-generated content: Tasks and related information, comments, questions, messages, and other content or information that you generate, transmit, or otherwise make available on the Service, as well as associated metadata.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Payment data: Information needed to complete transactions, including payment card information or bank account number.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Other data: Any other information not specifically listed here, which we will use as described in this Privacy Policy or as otherwise disclosed at the time of collection.
                        </Text>
                      </ListItem>
                    </UnorderedList>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      1.2. Third-Party Sources
                    </Heading>
                    <Text color={textColor}>
                      We may combine personal information we receive from you with personal information we obtain from other sources, such as:
                    </Text>
                    <UnorderedList mt={2} spacing={1} pl={5}>
                      <ListItem>
                        <Text color={textColor}>
                          Public sources: Public records, social media platforms, and other publicly available sources.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Private sources: Data providers or licensors.
                        </Text>
                      </ListItem>
                    </UnorderedList>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      1.3. Automatic Data Collection
                    </Heading>
                    <Text color={textColor}>
                      We, our service providers, and our business partners may automatically log information about you, your computer or mobile device, and your interactions over time with the Service, our communications, and other online services, such as:
                    </Text>
                    <UnorderedList mt={2} spacing={1} pl={5}>
                      <ListItem>
                        <Text color={textColor}>
                          Device data: Operating system type and version, manufacturer and model, browser type, screen resolution, RAM and disk size, CPU usage, device type (e.g., phone, tablet), IP address, unique identifiers, language settings, mobile device carrier, and general location information.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Online activity data: Pages or screens you viewed, time spent on a page or screen, the website visited before browsing to the Service, navigation paths between pages or screens, access times and duration of access, and email engagement data.
                        </Text>
                      </ListItem>
                    </UnorderedList>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      1.4. Cookies and Similar Technologies
                    </Heading>
                    <Text color={textColor}>
                      Some of the automatic collection described above is facilitated by technologies such as:
                    </Text>
                    <UnorderedList mt={2} spacing={1} pl={5}>
                      <ListItem>
                        <Text color={textColor}>
                          Cookies: Small text files that websites store on user devices, allowing web servers to record users&apos; web browsing activities and remember their submissions, preferences, and login status.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Local storage technologies: HTML5, which provides cookie-equivalent functionality but can store larger amounts of data on your device.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Web beacons: Also known as pixel tags or clear GIFs, used to demonstrate that a webpage or email was accessed or opened, or that certain content was viewed or clicked.
                        </Text>
                      </ListItem>
                    </UnorderedList>
                  </Box>
                </VStack>
              </Box>

              <Divider borderColor={dividerColor} />

              <Box width="full">
                <Heading as="h2" size="lg" mb={4} color={headingColor}>
                  2. How We Use Your Personal Information
                </Heading>
                <Text color={textColor}>
                  We may use your personal information for the following purposes or as otherwise described at the time of collection:
                </Text>

                <VStack spacing={4} align="flex-start" mt={4}>
                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      2.1. Service Delivery and Business Operations
                    </Heading>
                    <Text color={textColor}>
                      We may use your personal information to:
                    </Text>
                    <UnorderedList mt={2} spacing={1} pl={5}>
                      <ListItem>
                        <Text color={textColor}>
                          Provide, operate, and improve the Service and our business.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Establish and maintain your user profile on the Service.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Facilitate your invitations to friends or colleagues to join the Service.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Enable security features of the Service.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Communicate with you about the Service, including by sending announcements, updates, security alerts, and support and administrative messages.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Understand your needs and interests, and personalize your experience with the Service and our communications.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Provide support for the Service, and respond to your requests, questions, and feedback.
                        </Text>
                      </ListItem>
                    </UnorderedList>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      2.2. Research and Development
                    </Heading>
                    <Text color={textColor}>
                      We may use personal information for research and development purposes, including to analyze and improve the Service and our business. We may create aggregated, de-identified, or anonymized data from personal information we collect and share it with third parties for lawful business purposes.
                    </Text>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      2.3. Marketing
                    </Heading>
                    <Text color={textColor}>
                      We, our service providers, and our third-party partners may collect and use your personal information for marketing purposes. You may opt out of our marketing communications as described in Your Choices.
                    </Text>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      2.4. Compliance and Protection
                    </Heading>
                    <Text color={textColor}>
                      We may use your personal information to:
                    </Text>
                    <UnorderedList mt={2} spacing={1} pl={5}>
                      <ListItem>
                        <Text color={textColor}>
                          Comply with applicable laws, lawful requests, and legal processes.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Protect our, your, or others&apos; rights, privacy, safety, or property.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Audit our internal processes for compliance with legal and contractual requirements.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Enforce the terms and conditions that govern the Service.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Prevent, identify, investigate, and deter fraudulent, harmful, unauthorized, or illegal activity, including cyberattacks and identity theft.
                        </Text>
                      </ListItem>
                    </UnorderedList>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      2.5. With Your Consent
                    </Heading>
                    <Text color={textColor}>
                      In some cases, we may ask for your consent to collect, use, or share your personal information, such as when required by law.
                    </Text>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      2.6. Cookies and Similar Technologies
                    </Heading>
                    <Text color={textColor}>
                      We may use the cookies and similar technologies described above for the following purposes:
                    </Text>
                    <UnorderedList mt={2} spacing={1} pl={5}>
                      <ListItem>
                        <Text color={textColor}>
                          Technical operation: To allow the technical operation of the Service, such as remembering your selections and preferences as you navigate the site, and whether you are logged in when you visit password-protected areas of the Service.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Functionality: To enhance the performance and functionality of our services.
                        </Text>
                      </ListItem>
                      <ListItem>
                        <Text color={textColor}>
                          Analytics: To help us understand user activity on the Service, including which pages are most and least visited and how visitors move around the Service, as well as interactions with our emails.
                        </Text>
                      </ListItem>
                    </UnorderedList>
                  </Box>
                </VStack>
              </Box>

              <Divider borderColor={dividerColor} />

              <Box width="full">
                <Heading as="h2" size="lg" mb={4} color={headingColor}>
                  3. How We Share Your Personal Information
                </Heading>
                <Text color={textColor}>
                  We may share your personal information with the following parties and as otherwise described in this Privacy Policy or at the time of collection:
                </Text>
                <UnorderedList mt={2} spacing={1} pl={5}>
                  <ListItem>
                    <Text color={textColor}>
                      Affiliates: Our corporate parent, subsidiaries, and affiliates, for purposes consistent with this Privacy Policy.
                    </Text>
                  </ListItem>
                  <ListItem>
                    <Text color={textColor}>
                      Service Providers: Third parties that provide services on our behalf or help us operate the Service or our business (e.g., hosting, IT, customer support, email delivery, marketing, consumer research, and website analytics).
                    </Text>
                  </ListItem>
                  <ListItem>
                    <Text color={textColor}>
                      Payment Processors: Any payment card information you use to make a purchase on the Service is collected and processed directly by our payment processors (e.g., Stripe).
                    </Text>
                  </ListItem>
                  <ListItem>
                    <Text color={textColor}>
                      Professional Advisors: Lawyers, auditors, bankers, and insurers, where necessary in the course of the professional services they render to us.
                    </Text>
                  </ListItem>
                  <ListItem>
                    <Text color={textColor}>
                      Authorities and Others: Law enforcement, government authorities, and private parties, as we believe in good faith to be necessary or appropriate for compliance and protection purposes described above.
                    </Text>
                  </ListItem>
                  <ListItem>
                    <Text color={textColor}>
                      Business Transferees: Acquirers and other relevant participants in business transactions (e.g., merger, acquisition, financing, or sale of assets).
                    </Text>
                  </ListItem>
                  <ListItem>
                    <Text color={textColor}>
                      Other Users and the Public: Your profile and other user-generated content may be visible to other users of the Service in your same Account. This information can be seen, collected, and used by others, and we are not responsible for any such use, if you choose to make your profile or other personal information available to them through the Service.
                    </Text>
                  </ListItem>
                </UnorderedList>
              </Box>

              <Divider borderColor={dividerColor} />

              <Box width="full">
                <Heading as="h2" size="lg" mb={4} color={headingColor}>
                  4. Google User Data
                </Heading>
                <Text color={textColor}>
                  Our Service may integrate with certain Google Workspace APIs (e.g., Gmail, Calendar, Drive) to enhance productivity, facilitate communications, or otherwise improve user experience. When you connect your Google account and grant us permission to access your data, the following terms apply:
                </Text>

                <Box mt={4}>
                  <Heading as="h3" size="md" mb={2} color={headingColor}>
                    1. Limited Use:
                  </Heading>
                  <UnorderedList mt={2} spacing={1} pl={5}>
                    <ListItem>
                      <Text color={textColor}>
                        We access and use Google user data only to provide or improve our Service&apos;s features and functionality for you.
                      </Text>
                    </ListItem>
                    <ListItem>
                      <Text color={textColor}>
                        We will not transfer this data to third parties except as necessary to provide or improve the Service, or as required by law.
                      </Text>
                    </ListItem>
                    <ListItem>
                      <Text color={textColor}>
                        We do not use Google user data for advertising purposes or to build advertising profiles.
                      </Text>
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box mt={4}>
                  <Heading as="h3" size="md" mb={2} color={headingColor}>
                    2. Data Retention and Deletion:
                  </Heading>
                  <UnorderedList mt={2} spacing={1} pl={5}>
                    <ListItem>
                      <Text color={textColor}>
                        We retain Google user data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy or as otherwise required by law.
                      </Text>
                    </ListItem>
                    <ListItem>
                      <Text color={textColor}>
                        You may request deletion of any Google user data we hold about you by contacting us. Upon receiving a verified request, we will delete your data from our systems (and instruct our service providers to do the same) unless we are legally required to retain it.
                      </Text>
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box mt={4}>
                  <Heading as="h3" size="md" mb={2} color={headingColor}>
                    3. Data Protection:
                  </Heading>
                  <UnorderedList mt={2} spacing={1} pl={5}>
                    <ListItem>
                      <Text color={textColor}>
                        We store all Google user data using industry-standard security measures, including access controls and encryption in transit and at rest, to protect against unauthorized access or disclosure.
                      </Text>
                    </ListItem>
                    <ListItem>
                      <Text color={textColor}>
                        Access to Google user data within our organization is restricted on a need-to-know basis, and staff members are trained on confidentiality and security practices.
                      </Text>
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box mt={4}>
                  <Heading as="h3" size="md" mb={2} color={headingColor}>
                    4. Use for AI/ML Training:
                  </Heading>
                  <UnorderedList mt={2} spacing={1} pl={5}>
                    <ListItem>
                      <Text color={textColor}>
                        We do not use Google user data obtained through Workspace APIs to develop or improve generalized artificial intelligence (AI) or machine learning (ML) models.
                      </Text>
                    </ListItem>
                    <ListItem>
                      <Text color={textColor}>
                        Any AI or ML features within our Service that process Google user data do so only for the direct benefit of providing the Service to you. Any temporary processing that might involve AI is not used to build or train separate, generalized models.
                      </Text>
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Text mt={4} color={textColor}>
                  By connecting your Google account, you acknowledge and agree that your Google user data will be handled in accordance with this Privacy Policy and Google&apos;s API Services User Data Policy.
                </Text>
              </Box>

              <Divider borderColor={dividerColor} />

              <Box width="full">
                <Heading as="h2" size="lg" mb={4} color={headingColor}>
                  5. Your Choices
                </Heading>
                <Text color={textColor}>
                  In this section, we describe the rights and choices available to all users:
                </Text>

                <VStack spacing={4} align="flex-start" mt={4}>
                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      5.1. Access or Update Your Information
                    </Heading>
                    <Text color={textColor}>
                      If you have registered for an account with us through the Service, you may review and update certain account information by logging into the account or contacting us directly.
                    </Text>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      5.2. Opt-Out of Marketing Communications
                    </Heading>
                    <Text color={textColor}>
                      You may opt out of marketing-related emails by following the opt-out or unsubscribe instructions at the bottom of the email or by contacting us at the address below. You may continue to receive service-related and other non-marketing emails.
                    </Text>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      5.3. Cookies
                    </Heading>
                    <Text color={textColor}>
                      Most browsers let you remove or reject cookies placed by websites. Many browsers accept cookies by default until you change your settings. If you do not accept cookies, the Service may not function properly. For more information about cookies, including how to see what cookies have been set on your browser and how to manage and delete them, visit www.allaboutcookies.org.
                    </Text>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      5.4. Do Not Track
                    </Heading>
                    <Text color={textColor}>
                      Some Internet browsers may be configured to send &quot;Do Not Track&quot; signals to the online services that you visit. We currently do not respond to &quot;Do Not Track&quot; signals. For more information about &quot;Do Not Track,&quot; visit www.allaboutdnt.com.
                    </Text>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      5.5. Declining to Provide Information
                    </Heading>
                    <Text color={textColor}>
                      We need to collect personal information to provide certain services. If you do not provide the information identified as required or mandatory, we may not be able to provide those services.
                    </Text>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={headingColor}>
                      5.6. Request Deletion
                    </Heading>
                    <Text color={textColor}>
                      You may request that we delete your personal information by contacting us at the address listed below. We will take reasonable steps to promptly delete such data from our systems, subject to legal retention requirements.
                    </Text>
                  </Box>
                </VStack>
              </Box>

              <Divider borderColor={dividerColor} />

              <Box width="full">
                <Heading as="h2" size="lg" mb={4} color={headingColor}>
                  6. Data Security
                </Heading>
                <Text color={textColor}>
                  We employ a range of organizational, technical, and administrative measures to protect personal information within our organization. These include:
                </Text>
                <UnorderedList mt={2} spacing={1} pl={5}>
                  <ListItem>
                    <Text color={textColor}>
                      Encryption in Transit and at Rest: We use TLS/SSL to encrypt data in transit and maintain industry-standard encryption for stored data.
                    </Text>
                  </ListItem>
                  <ListItem>
                    <Text color={textColor}>
                      Access Controls: We restrict access to personal data to authorized personnel who require it for their job duties.
                    </Text>
                  </ListItem>
                  <ListItem>
                    <Text color={textColor}>
                      Staff Training: Employees receive training on privacy and data protection to ensure compliance with relevant laws and internal policies.
                    </Text>
                  </ListItem>
                  <ListItem>
                    <Text color={textColor}>
                      Incident Response: We maintain an incident response plan to handle any security events or breaches swiftly and effectively.
                    </Text>
                  </ListItem>
                </UnorderedList>
                <Text mt={4} color={textColor}>
                  However, no security measure is 100% perfect, and we cannot guarantee absolute security.
                </Text>
              </Box>

              <Divider borderColor={dividerColor} />

              <Box width="full">
                <Heading as="h2" size="lg" mb={4} color={headingColor}>
                  7. International Data Transfers
                </Heading>
                <Text color={textColor}>
                  We may transfer your personal information to jurisdictions outside of your state, province, or country. These jurisdictions may not have the same data protection laws as your jurisdiction. When we transfer your personal information internationally, we take steps to ensure it is protected in accordance with this Privacy Policy and applicable laws, which may include implementing appropriate data transfer mechanisms.
                </Text>
              </Box>

              <Divider borderColor={dividerColor} />

              <Box width="full">
                <Heading as="h2" size="lg" mb={4} color={headingColor}>
                  8. Retention of Personal Information
                </Heading>
                <Text color={textColor}>
                  We retain personal information (including Google user data) only as long as needed to fulfill the purposes for which we collected it or for other legitimate business needs, including to comply with our legal, regulatory, or contractual obligations. Once retention of such data is no longer required, we will either delete or anonymize your data, or if this is not possible (for example, because the information has been stored in backup archives), then we will securely store your data and isolate it from any further use until deletion is possible.
                </Text>
              </Box>

              <Divider borderColor={dividerColor} />

              <Box width="full">
                <Heading as="h2" size="lg" mb={4} color={headingColor}>
                  9. Children&apos;s Privacy
                </Heading>
                <Text color={textColor}>
                  The Service is not directed to, and we do not knowingly collect personal information from, anyone under the age of 13 (or other age as required by local law). If you learn that your minor child has provided us with personal information without your consent, you may contact us as described below. If we learn that we have collected any personal information from children, we will promptly take steps to delete such information.
                </Text>
              </Box>

              <Divider borderColor={dividerColor} />

              <Box width="full">
                <Heading as="h2" size="lg" mb={4} color={headingColor}>
                  10. Updates to This Privacy Policy
                </Heading>
                <Text color={textColor}>
                  We may modify this Privacy Policy at any time. When we make changes to this Privacy Policy, we will post the updated policy on the Service and update the &quot;Updated&quot; date at the top of this Privacy Policy. If we make material changes to this Privacy Policy, we will notify you by email or through the Service.
                </Text>
              </Box>

              <Divider borderColor={dividerColor} />

              <Box width="full">
                <Heading as="h2" size="lg" mb={4} color={headingColor}>
                  11. Contact Us
                </Heading>
                <Text color={textColor}>
                  If you have any questions or concerns about this Privacy Policy or our privacy practices, or to exercise any of your rights and choices as described above, please contact us at:
                </Text>
                <Text mt={2} fontWeight="medium" color={headingColor}>
                  Email: <Link href="mailto:admin@lysto.ai" color="blue.500">admin@lysto.ai</Link>
                </Text>
                <Text mt={4} color={textColor}>
                  By using the Service or otherwise providing us with personal information, you agree to the practices described in this Privacy Policy.
                </Text>
              </Box>
            </VStack>
          </VStack>
        </Container>
      </Box>
      <LandingFooter />
    </Box>
  );
};

export default PrivacyPolicy; 